import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import Panel from "../components/Panel";
import KpiCard from "../components/KpiCard";
import { fmtINR } from "../lib/format";
import { ResponsiveContainer, RadialBarChart, RadialBar, PolarAngleAxis, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell } from "recharts";
import { AlertTriangle, CheckCircle2, Zap, ShieldAlert, Brain } from "lucide-react";

const AMBER = "#fb8b1e";
const POS = "#4af6c3";
const NEG = "#ff433d";
const NEU = "#0068ff";

const gradeColor = (g) => ({ "A+": POS, "A": POS, "B": AMBER, "C": "#ff8c42", "D": NEG }[g] || AMBER);

export default function Intelligence() {
    const [data, setData] = useState(null);
    const [briefing, setBriefing] = useState(null);

    useEffect(() => {
        api.get("/intelligence/scores").then((r) => setData(r.data));
        api.get("/intelligence/briefing").then((r) => setBriefing(r.data));
    }, []);

    if (!data) return <div className="font-mono-fin text-sm text-[var(--text-secondary)]">// LOADING PREDICTIVE INTELLIGENCE...</div>;

    const health = data.company_health;
    const gaugeData = [{ name: "Health", value: health.score, fill: gradeColor(health.grade) }];

    return (
        <div className="space-y-4">
            <div>
                <div className="font-mono-fin text-[10px] uppercase tracking-[0.25em] text-[var(--brand-amber)] mb-1">// PREDICTIVE INTELLIGENCE</div>
                <h1 className="font-display font-black text-3xl flex items-center gap-3"><Brain className="text-[var(--brand-amber)]" size={26} /> Signal, Not Noise</h1>
                <p className="mt-1 font-mono-fin text-[11px] text-[var(--text-secondary)] uppercase tracking-wider">Forward-looking scores · Risk detection · Weighted forecasts</p>
            </div>

            {/* Executive briefing */}
            {briefing && (
                <Panel testId="ex-briefing" title="Executive Briefing" subtitle={`${briefing.period} · Refreshed live`} right={
                    <Link to="/ai-advisor" data-testid="briefing-elaborate" className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--brand-amber)] flex items-center gap-1">
                        <Zap size={11} /> Elaborate with AI
                    </Link>
                }>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                        <div>
                            <div className="text-[10px] font-mono-fin uppercase tracking-[0.15em] text-pos mb-2">✓ Wins</div>
                            <ul className="space-y-2 text-xs text-[var(--text-primary)] font-body leading-relaxed">
                                {briefing.wins.map((w, i) => <li key={i} className="flex gap-2"><span className="text-pos shrink-0">▸</span>{w}</li>)}
                            </ul>
                        </div>
                        <div>
                            <div className="text-[10px] font-mono-fin uppercase tracking-[0.15em] text-neg mb-2">⚠ Concerns</div>
                            <ul className="space-y-2 text-xs text-[var(--text-primary)] font-body leading-relaxed">
                                {briefing.concerns.map((c, i) => <li key={i} className="flex gap-2"><span className="text-neg shrink-0">▸</span>{c}</li>)}
                            </ul>
                        </div>
                        <div>
                            <div className="text-[10px] font-mono-fin uppercase tracking-[0.15em] text-amber mb-2">◈ Bold Move</div>
                            <div className="border border-[var(--brand-amber)] p-3 bg-[var(--brand-amber-dim)]">
                                <div className="text-xs text-[var(--text-primary)] font-body leading-relaxed">{briefing.bold_move}</div>
                            </div>
                            <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-muted)] mt-3">{briefing.next_review}</div>
                        </div>
                    </div>
                </Panel>
            )}

            {/* Composite scores row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Panel testId="composite-health" title="Company Health Score" subtitle="Composite (0-100)">
                    <div className="flex items-center gap-4">
                        <div className="w-32 h-32 shrink-0">
                            <ResponsiveContainer width="100%" height="100%">
                                <RadialBarChart cx="50%" cy="50%" innerRadius="70%" outerRadius="100%" data={gaugeData} startAngle={225} endAngle={-45}>
                                    <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
                                    <RadialBar dataKey="value" cornerRadius={0} background={{ fill: "#E2E8F0" }} />
                                </RadialBarChart>
                            </ResponsiveContainer>
                        </div>
                        <div>
                            <div className="font-mono-fin text-5xl font-bold" style={{ color: gradeColor(health.grade) }}>{health.score}</div>
                            <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)]">Grade {health.grade}</div>
                            <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-muted)] mt-2">Weighted composite of 5 pillars</div>
                        </div>
                    </div>
                    <div className="mt-4 space-y-2">
                        {health.components.map((c) => (
                            <div key={c.name}>
                                <div className="flex justify-between text-[10px] font-mono-fin uppercase tracking-wider">
                                    <span className="text-[var(--text-secondary)]">{c.name}</span>
                                    <span className="text-[var(--text-primary)]">{c.value.toFixed(2)} <span className="text-[var(--text-muted)]">({c.weight}%)</span></span>
                                </div>
                                <div className="h-1 bg-[var(--bg-base)] border border-panel mt-1">
                                    <div className="h-full bg-[var(--brand-amber)]" style={{ width: `${Math.min(100, (c.value / (c.name.includes("Altman") ? 3 : c.name.includes("DSCR") ? 2 : 100)) * 100)}%` }} />
                                </div>
                            </div>
                        ))}
                    </div>
                </Panel>

                <Panel testId="cash-runway" title="Cash Runway" subtitle="Days of survival at current burn">
                    <div className="text-center py-4">
                        <div className="font-mono-fin text-6xl font-bold" style={{ color: data.cash_runway.status === "Critical" ? NEG : data.cash_runway.status === "Watchlist" ? AMBER : POS }}>
                            {data.cash_runway.days}
                        </div>
                        <div className="text-[10px] font-mono-fin uppercase tracking-[0.15em] text-[var(--text-secondary)] mt-1">Days</div>
                        <div className="text-[10px] font-mono-fin uppercase tracking-wider mt-3" style={{ color: data.cash_runway.status === "Critical" ? NEG : data.cash_runway.status === "Watchlist" ? AMBER : POS }}>
                            ● {data.cash_runway.status}
                        </div>
                    </div>
                    <div className="border-t border-panel pt-3 mt-3 space-y-2 text-[11px] font-mono-fin">
                        <div className="flex justify-between"><span className="text-[var(--text-secondary)] uppercase tracking-wider">Cash-on-Hand</span><span className="text-[var(--text-primary)]">{fmtINR(data.cash_runway.cash)}</span></div>
                        <div className="flex justify-between"><span className="text-[var(--text-secondary)] uppercase tracking-wider">Monthly Burn</span><span className="text-neg">{fmtINR(data.cash_runway.monthly_burn)}</span></div>
                        <div className="flex justify-between"><span className="text-[var(--text-secondary)] uppercase tracking-wider">Runway</span><span className="text-amber">{data.cash_runway.months} months</span></div>
                    </div>
                    <Link to="/simulator" className="mt-3 block text-center text-[10px] font-mono-fin uppercase tracking-wider text-[var(--brand-amber)] hover:underline border border-panel py-2 hover:border-[var(--brand-amber)]">
                        Simulate impact →
                    </Link>
                </Panel>

                <Panel testId="distress-score" title="Financial Distress Score" subtitle="Altman Z + Beneish M">
                    <div className="text-center py-4">
                        <div className="font-mono-fin text-6xl font-bold" style={{ color: data.distress.zone === "Safe" ? POS : data.distress.zone === "Grey" ? AMBER : NEG }}>
                            {data.distress.altman_z.toFixed(2)}
                        </div>
                        <div className="text-[10px] font-mono-fin uppercase tracking-[0.15em] text-[var(--text-secondary)] mt-1">Altman Z-Score</div>
                        <div className="text-[10px] font-mono-fin uppercase tracking-wider mt-3" style={{ color: data.distress.zone === "Safe" ? POS : data.distress.zone === "Grey" ? AMBER : NEG }}>
                            ● {data.distress.zone} Zone
                        </div>
                    </div>
                    <div className="border-t border-panel pt-3 mt-3 space-y-2 text-[11px] font-mono-fin">
                        <div className="flex justify-between"><span className="text-[var(--text-secondary)] uppercase tracking-wider">Beneish M</span><span className="text-[var(--text-primary)]">{data.distress.beneish_m}</span></div>
                        <div className="flex justify-between"><span className="text-[var(--text-secondary)] uppercase tracking-wider">Verdict</span><span className="text-amber">{data.distress.flag}</span></div>
                    </div>
                    <div className="mt-3 text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-muted)] leading-relaxed">
                        Safe &gt; 2.99 · Grey 1.81-2.99 · Distress &lt; 1.81
                    </div>
                </Panel>
            </div>

            {/* Project delay probability */}
            <Panel testId="project-delay-scores" title="Project Delay Probability" subtitle="AI-derived from SPI × CPI × Risk × Status">
                <div className="h-56 mb-3">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={data.project_delays} layout="vertical" margin={{ left: 60 }}>
                            <CartesianGrid stroke="#E2E8F0" strokeOpacity={0.4} horizontal={false} />
                            <XAxis type="number" tick={{ fill: "#8892B0", fontSize: 10 }} domain={[0, 100]} unit="%" />
                            <YAxis type="category" dataKey="code" tick={{ fill: "#8892B0", fontSize: 10 }} width={110} />
                            <Tooltip formatter={(v) => `${v}%`} />
                            <Bar dataKey="delay_probability" name="Delay Probability">
                                {data.project_delays.map((d, i) => (
                                    <Cell key={i} fill={d.delay_probability >= 60 ? NEG : d.delay_probability >= 35 ? AMBER : POS} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>
                <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-muted)]">
                    Red &gt; 60% risk · Amber 35-60% · Green &lt; 35%. Click a project for turnaround plan.
                </div>
            </Panel>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
                {/* Client risk scoring */}
                <Panel testId="client-risk-scores" title="Client Payment Risk Grades" subtitle="Predicted DSO + payment behaviour">
                    <div className="overflow-x-auto">
                        <table className="w-full font-mono-fin text-xs">
                            <thead className="border-b border-panel">
                                <tr className="text-[10px] uppercase tracking-wider text-[var(--text-secondary)]">
                                    <th className="text-left py-2">Client</th>
                                    <th className="text-center">Grade</th>
                                    <th className="text-right">Score</th>
                                    <th className="text-right">Predicted DSO</th>
                                    <th className="text-left">Flag</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.client_risks.map((c) => (
                                    <tr key={c.client_id} className="border-b border-[var(--border)]/50 hover:bg-[var(--bg-panel-hover)]">
                                        <td className="py-2 text-[var(--text-primary)]">{c.name}</td>
                                        <td className="text-center">
                                            <span className="text-sm font-bold px-2 py-0.5 border" style={{ color: gradeColor(c.grade), borderColor: gradeColor(c.grade) }}>{c.grade}</span>
                                        </td>
                                        <td className="text-right" style={{ color: gradeColor(c.grade) }}>{c.score}</td>
                                        <td className={`text-right ${c.predicted_dso > 90 ? "text-neg" : c.predicted_dso > 60 ? "text-amber" : "text-[var(--text-primary)]"}`}>{c.predicted_dso}d</td>
                                        <td className="text-[10px] uppercase tracking-wider" style={{ color: c.flag === "Healthy" ? POS : c.flag.includes("Disputed") || c.flag.includes("Overdue") ? NEG : AMBER }}>{c.flag}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Panel>

                {/* Tender prediction */}
                <Panel testId="tender-predictions" title="Refined Tender Win Predictions" subtitle="Base × Stage × Margin uplift">
                    <div className="overflow-x-auto">
                        <table className="w-full font-mono-fin text-xs">
                            <thead className="border-b border-panel">
                                <tr className="text-[10px] uppercase tracking-wider text-[var(--text-secondary)]">
                                    <th className="text-left py-2">Tender</th>
                                    <th className="text-right">Base</th>
                                    <th className="text-right">Refined</th>
                                    <th className="text-right">Exp. Profit</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.tender_predictions.map((t) => (
                                    <tr key={t.id} className="border-b border-[var(--border)]/50 hover:bg-[var(--bg-panel-hover)]">
                                        <td className="py-2 text-[var(--text-primary)]">
                                            <div className="text-xs">{t.name}</div>
                                            <div className="text-[9px] text-[var(--text-muted)] uppercase tracking-wider">{t.authority}</div>
                                        </td>
                                        <td className="text-right text-[var(--text-secondary)]">{t.base_prob}%</td>
                                        <td className={`text-right ${t.refined_prob >= 55 ? "text-pos" : t.refined_prob >= 35 ? "text-amber" : "text-neg"}`}>{t.refined_prob}%</td>
                                        <td className="text-right text-amber">{fmtINR(t.expected_profit)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Panel>
            </div>
        </div>
    );
}
